import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { neoApi, api, type LinkedRepo, type DocSource } from '../api';
import { Page, Card, EmptyState, Button, Select, Field } from '../ui';

/**
 * Artefatos — a vitrine do que o projeto ENTREGOU (site, publicações, assistentes,
 * documentos), consolidada de várias fontes. É a tela principal de entregáveis; o
 * editor de arquivos (Documentos) fica a um clique de distância.
 */

interface ArtifactsData {
  docs: { file: string; title: string }[];
  site: string | null;
  publications: number;
  agents: { id: string; name: string; description: string }[];
}

// Nomes amigáveis pros documentos conhecidos (o resto usa o título do arquivo).
const DOC_NAMES: Record<string, string> = {
  '00-diagnostico.md': 'Diagnóstico',
  '01-posicionamento.md': 'Posicionamento',
  '02-growth.md': 'Plano de crescimento',
  '04-brandguide.md': 'Identidade da marca',
  '05-campanhas.md': 'Campanhas',
  '_projeto.md': 'Estado do projeto',
};

const cardStyle: React.CSSProperties = {
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md, 10px)',
  padding: 'var(--space-3) var(--space-4)',
  background: 'var(--color-bg)',
  textDecoration: 'none',
  color: 'var(--color-fg)',
  display: 'block',
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 'var(--space-5)' }}>
      <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-fg-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 'var(--space-3)' }}>
        {title}
      </div>
      {children}
    </div>
  );
}

/**
 * Fontes da base de conhecimento: repos vinculados + pastas vinculadas. O usuário
 * escolhe um repo (da lista do projeto) + uma pasta, e ela passa a aparecer em
 * "Documentos". Editar um doc dali = commit/push no repo (publicação em lote).
 */
function DocSourcesManager({ slug }: { slug: string }) {
  const [repos, setRepos] = useState<LinkedRepo[]>([]);
  const [sources, setSources] = useState<DocSource[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // form "vincular pasta"
  const [repoId, setRepoId] = useState('');
  const [branches, setBranches] = useState<string[]>([]);
  const [branch, setBranch] = useState('');
  const [folders, setFolders] = useState<string[]>([]);
  const [folder, setFolder] = useState('');
  const [navBusy, setNavBusy] = useState(false);
  const [busy, setBusy] = useState(false);

  // form "vincular repositório"
  const [showRepoForm, setShowRepoForm] = useState(false);
  const [newRepoUrl, setNewRepoUrl] = useState('');
  const [newRepoLabel, setNewRepoLabel] = useState('');

  function reload() {
    api.listRepos(slug).then((r) => setRepos(r.repos)).catch(() => undefined);
    api.listDocSources(slug).then((r) => setSources(r.sources)).catch(() => undefined);
  }
  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  async function chooseBranch(id: string, b: string) {
    setBranch(b);
    setFolder('');
    setFolders([]);
    setNavBusy(true);
    setErr(null);
    try {
      const f = await api.repoFolders(slug, id, b);
      setFolders(f.folders);
    } catch {
      setErr('Não consegui ler as pastas desse repo/branch.');
    } finally {
      setNavBusy(false);
    }
  }

  async function chooseRepo(id: string) {
    setRepoId(id);
    setBranch('');
    setFolders([]);
    setFolder('');
    setBranches([]);
    if (!id) return;
    const repo = repos.find((r) => r.id === id);
    setNavBusy(true);
    setErr(null);
    try {
      const b = await api.repoBranches(slug, id);
      setBranches(b.branches);
      const def = repo && repo.defaultBranch && b.branches.includes(repo.defaultBranch) ? repo.defaultBranch : b.branches[0];
      if (def) await chooseBranch(id, def);
    } catch {
      setErr('Não consegui ler as branches desse repo (token/URL corretos?).');
    } finally {
      setNavBusy(false);
    }
  }

  async function vincularPasta() {
    if (!repoId || !branch || !folder || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await api.addDocSource(slug, { repoId, branch, path: folder });
      setMsg(r.created ? `Pasta "${folder}" vinculada à base.` : 'Essa pasta já estava vinculada.');
      setFolder('');
      reload();
    } catch {
      setErr('Falha ao vincular a pasta.');
    } finally {
      setBusy(false);
    }
  }

  async function addRepo() {
    const url = newRepoUrl.trim();
    if (!url || busy) return;
    setBusy(true);
    setErr(null);
    try {
      await api.linkRepo(slug, { repoUrl: url, ...(newRepoLabel.trim() ? { label: newRepoLabel.trim() } : {}) });
      setNewRepoUrl('');
      setNewRepoLabel('');
      setShowRepoForm(false);
      setMsg('Repositório vinculado.');
      reload();
    } catch {
      setErr('Falha ao vincular o repositório (URL válida?).');
    } finally {
      setBusy(false);
    }
  }

  async function removeSource(id: string) {
    await api.removeDocSource(slug, id).catch(() => undefined);
    reload();
  }

  return (
    <Card style={{ marginBottom: 'var(--space-5)' }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>📚 Base de conhecimento — pastas vinculadas</div>
      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-fg-muted)', marginBottom: 'var(--space-3)' }}>
        Escolha um repositório do projeto e uma pasta dele. Ela aparece em <strong>Documentos</strong>, e os agentes leem/editam de lá.
      </div>

      {err && <div className="chat-error" style={{ marginBottom: 8 }}>{err}</div>}
      {msg && <div className="agent-editor-msg" style={{ marginBottom: 8 }}>{msg}</div>}

      {/* Fontes já vinculadas */}
      {sources.length > 0 ? (
        <div style={{ display: 'grid', gap: 6, marginBottom: 'var(--space-3)' }}>
          {sources.map((s) => (
            <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid var(--color-border)', borderRadius: 8, padding: '6px 10px' }}>
              <span style={{ fontSize: 16 }}>📁</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{s.repoLabel} ▸ /{s.path}</div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-fg-muted)' }}>branch {s.branch}</div>
              </div>
              <Link to={`/p/${slug}/knowledge`} style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-accent)' }}>abrir →</Link>
              <Button variant="ghost" onClick={() => removeSource(s.id)} title="Desvincular (não apaga nada no repo)">✕</Button>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-fg-muted)', marginBottom: 'var(--space-3)' }}>Nenhuma pasta vinculada ainda.</div>
      )}

      {/* Vincular nova pasta */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)', alignItems: 'end' }}>
        <Field label="Repositório">
          <Select value={repoId} onChange={(e) => chooseRepo(e.target.value)}>
            <option value="">— escolha um repo —</option>
            {repos.map((r) => (
              <option key={r.id} value={r.id}>{r.label || r.repoUrl}</option>
            ))}
          </Select>
        </Field>
        <Field label="Branch">
          <Select value={branch} onChange={(e) => chooseBranch(repoId, e.target.value)} disabled={!repoId || navBusy}>
            {branches.length === 0 && <option value="">—</option>}
            {branches.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </Select>
        </Field>
        <Field label="Pasta">
          <Select value={folder} onChange={(e) => setFolder(e.target.value)} disabled={!branch || navBusy}>
            <option value="">{navBusy ? 'carregando…' : '— escolha uma pasta —'}</option>
            {folders.map((f) => (
              <option key={f} value={f}>{f}</option>
            ))}
          </Select>
        </Field>
        <Button variant="primary" onClick={vincularPasta} disabled={!repoId || !branch || !folder || busy}>
          {busy ? 'vinculando…' : 'Vincular pasta'}
        </Button>
      </div>

      {/* Vincular repositório */}
      <div style={{ marginTop: 'var(--space-3)' }}>
        {!showRepoForm ? (
          <Button variant="ghost" onClick={() => setShowRepoForm(true)}>＋ Vincular outro repositório</Button>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto auto', gap: 'var(--space-2)', alignItems: 'end' }}>
            <Field label="URL do repositório (https://github.com/...)">
              <input value={newRepoUrl} onChange={(e) => setNewRepoUrl(e.target.value)} placeholder="https://github.com/org/repo" />
            </Field>
            <Field label="Nome (opcional)">
              <input value={newRepoLabel} onChange={(e) => setNewRepoLabel(e.target.value)} placeholder="Docs" />
            </Field>
            <Button variant="primary" onClick={addRepo} disabled={!newRepoUrl.trim() || busy}>Vincular</Button>
            <Button variant="ghost" onClick={() => setShowRepoForm(false)}>Cancelar</Button>
          </div>
        )}
      </div>
    </Card>
  );
}

export function Artifacts() {
  const { slug } = useParams<{ slug: string }>();
  const [data, setData] = useState<ArtifactsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDocConfig, setShowDocConfig] = useState(false); // config de fontes fica escondida por padrão

  useEffect(() => {
    if (!slug) return;
    neoApi
      .artifacts(slug)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const empty = data && !data.site && data.agents.length === 0 && data.docs.length === 0;

  return (
    <Page title="Artefatos" subtitle="Tudo que o projeto já entregou — site, publicações, assistentes e documentos.">
      {/* Configuração das fontes da base — escondida por padrão (é administrativa). */}
      {slug && (
        <div style={{ maxWidth: 860 }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: showDocConfig ? 'var(--space-3)' : 0 }}>
            <Button variant="ghost" onClick={() => setShowDocConfig((s) => !s)}>
              {showDocConfig ? '✕ Fechar configuração' : '⚙ Configurar fontes de documentação'}
            </Button>
          </div>
          {showDocConfig && <DocSourcesManager slug={slug} />}
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--color-fg-muted)' }}>Carregando…</div>
      ) : empty || !data ? (
        <EmptyState
          icon={<span style={{ fontSize: 32 }}>📦</span>}
          title="Nada entregue ainda"
          description="Conforme o projeto avança no Plano, os entregáveis (site, documentos, assistentes) aparecem aqui."
          action={<Link to={`/p/${slug}/plano`} style={{ color: 'var(--color-accent)' }}>Ir para o Plano do Projeto</Link>}
        />
      ) : (
        <div style={{ maxWidth: 860 }}>
          {/* Site */}
          {data.site && (
            <Section title="Site">
              <a href={data.site} target="_blank" rel="noreferrer" style={{ ...cardStyle, borderColor: 'var(--color-accent)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                  <span style={{ fontSize: 20 }}>🌐</span>
                  <div>
                    <div style={{ fontWeight: 600 }}>{data.site.replace(/^https?:\/\//, '')}</div>
                    <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-fg-muted)' }}>
                      No ar · {data.publications > 0 ? `${data.publications} publicação(ões)` : 'sem publicações ainda'} · abrir ↗
                    </div>
                  </div>
                </div>
              </a>
            </Section>
          )}

          {/* Assistentes */}
          {data.agents.length > 0 && (
            <Section title="Assistentes">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 'var(--space-3)' }}>
                {data.agents.map((a) => (
                  <Link key={a.id} to={`/p/${slug}/agents`} style={cardStyle}>
                    <div style={{ fontWeight: 600 }}>🤖 {a.name}</div>
                    <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-fg-muted)', marginTop: 2 }}>{a.description}</div>
                  </Link>
                ))}
              </div>
            </Section>
          )}

          {/* Documentos */}
          {data.docs.length > 0 && (
            <Section title="Documentos">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 'var(--space-3)' }}>
                {data.docs.map((d) => (
                  <Link key={d.file} to={`/p/${slug}/knowledge`} style={cardStyle}>
                    <div style={{ fontWeight: 600 }}>📄 {DOC_NAMES[d.file] ?? d.title}</div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-fg-muted)', marginTop: 2 }}>{d.file}</div>
                  </Link>
                ))}
              </div>
            </Section>
          )}
        </div>
      )}
    </Page>
  );
}
